import React, { useEffect, useState } from 'react';
import classes from './ResourceSelectionButton.module.scss';
import { ReactComponent as SearchIcon } from 'new/components/icons/search.svg';
import { Button } from 'new/elements/Button';
import { Stack } from 'new/elements/Stack';
import { ResourceDetailProvider } from 'new/components/ResourceDetailHandler/ResourceDetailProvider';
import { ResourceSelectionButtonProps } from 'new/elements/ResourceSelectionButton/types';
import { resourceNames } from 'new/elements/ResourceSelectionButton/consts';
import SelectedItem from 'new/elements/ResourceSelectionButton/components/SelectedItem';
import { ResourceSelection } from 'new/elements/ResourceSelectionButton/components/ResourceSelection';
import { Drawer } from 'new/elements/ResourceSelectionButton/components/Drawer';
import * as S from './ResourceSelectionButton.styled';

export const ResourceSelectionButton = (props: ResourceSelectionButtonProps) => {
  const {
    ButtonComponent,
    resourceType,
    defaultSelected,
    fixed = true,
    hideSelection,
    fullWidth = true,
  } = props;
  const name = resourceNames[resourceType];
  const [showOptions, setShowOptions] = useState(false);
  const [selected, setSelected] = useState(defaultSelected || []);

  useEffect(() => {
    setSelected(defaultSelected || []);
  }, []);

  const onClose = () => {
    if (props.onClose) {
      setTimeout(() => {
        // Why inside a timeout? To keep the animation working
        props.onClose();
      }, 500);
    }
    setShowOptions(false);
  };

  const onChooseLocal = (selected) => {
    setSelected(selected);
    props.onChoose(selected, resourceType);
    onClose();
  };

  const onClickRemove = (item) => () => {
    const newSelected = selected.filter((s) => s.id !== item.id);
    setSelected(newSelected);
    if (props.onRemove) {
      props.onRemove(item);
    } else {
      props.onChoose(newSelected, resourceType);
    }
  };

  const Container = fixed ? S.StyledContainerFixed : S.StyledContainer;

  const onClickShow = () => {
    if (props.onButtonClick) {
      props.onButtonClick();
    }
    setShowOptions(true);
  };

  return (
    <Stack width={fullWidth ? '100%' : undefined}>
      {ButtonComponent ? (
        React.cloneElement(ButtonComponent, {
          onClick: onClickShow,
        })
      ) : (
        <Button
          className={classes.alignSelfStart}
          size="small"
          variant="outlined"
          color="primary"
          onClick={onClickShow}
          startIcon={<SearchIcon />}
        >
          {props.label || `Find a ${name}`}
        </Button>
      )}
      {hideSelection ? null : (
        <Stack marginTop="32px" width="100%" gap="16px">
          {selected.map((s) => (
            <SelectedItem
              item={s}
              key={s.id}
              hideDetails={props.hideDetails}
              onClickRemove={onClickRemove}
              resourceType={resourceType}
              resourceName={name}
            />
          ))}
        </Stack>
      )}
      <Drawer fixed={fixed} onClose={onClose} open={showOptions}>
        <Container disableGutters>
          {showOptions ? (
            <ResourceSelection
              multi={props.multi}
              additionalActions={props.additionalActions}
              ExpandView={props.ExpandView}
              hidePreviewAction={props.hidePreviewAction}
              onClickPreview={props.onClickPreview}
              previewActionLabel={props.previewActionLabel}
              hideDetails={props.hideDetails}
              isItemDisabled={props.isItemDisabled}
              dataFeedCompatible={props.dataFeedCompatible}
              defaultSelected={selected}
              resourceName={name}
              onChoose={onChooseLocal}
              resourceType={resourceType}
              onClose={onClose}
            />
          ) : null}
        </Container>
      </Drawer>
    </Stack>
  );
};

export default (props: ResourceSelectionButtonProps) => (
  <ResourceDetailProvider hideActions resourceType={props.resourceType}>
    <ResourceSelectionButton {...props} />
  </ResourceDetailProvider>
);
